/**
 * SF-157 Components 1/2: groups the current scope's `IntelReportDto[]` into one `TargetGroup` per
 * scouted province (Intel Hub's actual job is target-scouting, not archive browsing — see the
 * spec's Overview), plus `deriveHeadlineStats()`, the sole source of truth for a target's headline
 * stats (Networth/Land/Offense/Defense/Gold/Runes/Food/Trade Balance/Raw TPA/Research
 * Rate/Home Defense).
 *
 * Crystal Eye reports are excluded from the result (kingdom-level event digests, not per-province
 * intel) — `groupIntelByTarget()` returns `TargetGroup[]` only; `IntelHubTab.tsx` derives its own
 * `crystalEyeReports` separately via `reports.filter(r => r.type === 'CE')`.
 */

import type {
  IntelReportDto,
  CrystalBallData,
  SpyOnMilitaryData,
  SurveyData,
  InfiltrateData,
  SpyOnScienceData,
  SpyOnHomeDefenseData,
  SpyOnThroneData,
} from '../types/intel';
import { parseIntelData } from '../types/intel';

export interface TargetGroup {
  targetProvinceId: string;
  targetProvinceName: string;
  targetKingdomLocation: string;
  race: string | null;
  personality: string | null;
  freshestCapturedAt: number;
  reports: IntelReportDto[];
}

export interface HeadlineStats {
  networth: number | null;
  land: number | null;
  offensePoints: number | null;
  defensePoints: number | null;
  gold: number | null;
  runes: number | null;
  food: number | null;
  tradeBalance: number | null;
  rawTPA: number | null;
  researchRate: string | null;
  homeDefense: number | null;
}

// ResearchRate.java's own declared order — NO_RESEARCH=0 ... EXTREME=9.
const RESEARCH_RATE_ORDER = [
  'NO_RESEARCH', 'MINIMAL', 'LIMITED', 'SUSTAINED', 'ACTIVE',
  'FOCUSED', 'ACCELERATED', 'INTENSIVE', 'RUSHED', 'EXTREME',
];

/** Indexes into `ResearchRate.java`'s own declared order. `null` in, `null` out. */
export function researchRateOrdinal(rate: string | null): number | null {
  if (rate == null) return null;
  const idx = RESEARCH_RATE_ORDER.indexOf(rate);
  return idx === -1 ? null : idx;
}

/**
 * Every real non-CE report always carries a real target (Codebase Context); `targetProvinceId` is
 * still typed `string | null` on `IntelReportDto` for every report type. A type-predicate guard
 * narrows without an unchecked `as string` cast — a (should-never-happen) non-CE report with a
 * `null` `targetProvinceId` is defensively excluded rather than crashing the whole list.
 */
function hasTarget(r: IntelReportDto): r is IntelReportDto & { targetProvinceId: string } {
  return r.type !== 'CE' && r.targetProvinceId != null;
}

/** Epoch milliseconds — never a lexicographic string comparison (variable ISO sub-second precision). */
function epochMillis(r: IntelReportDto): number {
  return Date.parse(r.capturedAt);
}

/** The single freshest report in a non-empty list. */
function freshestOf(reports: IntelReportDto[]): IntelReportDto {
  return reports.reduce((a, b) => (epochMillis(b) > epochMillis(a) ? b : a));
}

/** The freshest report of a given type, or `undefined` if the group has none. */
function freshestByType(reports: IntelReportDto[], type: IntelReportDto['type']): IntelReportDto | undefined {
  const typed = reports.filter((r) => r.type === type);
  return typed.length === 0 ? undefined : freshestOf(typed);
}

function raceOf(r: IntelReportDto): string | null {
  const parsed = parseIntelData(r);
  switch (parsed.type) {
    case 'CB':
      return (parsed.data as CrystalBallData).race ?? null;
    case 'SOM':
      return (parsed.data as SpyOnMilitaryData).race ?? null;
    case 'SOT':
      return (parsed.data as SpyOnThroneData).race ?? null;
    default:
      return null;
  }
}

function personalityOf(r: IntelReportDto): string | null {
  const parsed = parseIntelData(r);
  return parsed.type === 'CB' ? ((parsed.data as CrystalBallData).personality ?? null) : null;
}

/** Every type but CE carries `networth` (SF-157) — `null` when the report predates SF-157. */
function networthOf(r: IntelReportDto): number | null {
  const parsed = parseIntelData(r);
  if (parsed.type === 'CE') return null;
  const data = parsed.data as { networth?: number | null };
  return data.networth ?? null;
}

/**
 * Groups the given reports by target province. Reports of the same type are all kept (not
 * deduplicated to one-per-type) — this supports `MobileIntelDetailScreen`'s per-report stacking,
 * matching desktop.
 */
export function groupIntelByTarget(reports: IntelReportDto[]): TargetGroup[] {
  const byTarget = new Map<string, IntelReportDto[]>();
  for (const r of reports) {
    if (!hasTarget(r)) continue;
    const list = byTarget.get(r.targetProvinceId);
    if (list) {
      list.push(r);
    } else {
      byTarget.set(r.targetProvinceId, [r]);
    }
  }

  return Array.from(byTarget.entries()).map(([targetProvinceId, groupReports]) => {
    const freshestReport = freshestOf(groupReports);

    // race: freshest report among {CB, SOM, SOT} that carries it (a fixed province attribute, so
    // all three carriers agree in practice — this is completeness of the carrier list, not a
    // priority order).
    const raceCarriers = groupReports.filter((r) => raceOf(r) != null);
    const race = raceCarriers.length === 0 ? null : raceOf(freshestOf(raceCarriers));

    // personality: CB-only.
    const personalityCarriers = groupReports.filter((r) => personalityOf(r) != null);
    const personality = personalityCarriers.length === 0 ? null : personalityOf(freshestOf(personalityCarriers));

    return {
      targetProvinceId,
      targetProvinceName: freshestReport.targetProvinceName,
      targetKingdomLocation: freshestReport.targetKingdomLocation,
      race,
      personality,
      freshestCapturedAt: epochMillis(freshestReport),
      reports: groupReports,
    };
  });
}

/**
 * Computes, from whichever report types are present in the group, only the stats that are
 * actually available — every field is `null` when no present report type carries it. This is the
 * sole source of truth for a target's headline stats everywhere (list card, detail screen header,
 * sort) — `TargetGroup` itself carries no `networth` field to avoid a second, driftable source.
 */
export function deriveHeadlineStats(group: TargetGroup): HeadlineStats {
  const { reports } = group;

  // Networth: freshest report of any (non-CE) type that carries a non-null networth.
  const networthCarriers = reports.filter((r) => networthOf(r) != null);
  const networth = networthCarriers.length === 0 ? null : networthOf(freshestOf(networthCarriers));

  // Land: CB's totalLand if present, else Survey's totalLand.
  const latestCb = freshestByType(reports, 'CB');
  const latestSurvey = freshestByType(reports, 'SURVEY');
  const land = latestCb
    ? (parseIntelData(latestCb).data as CrystalBallData).totalLand
    : latestSurvey
      ? (parseIntelData(latestSurvey).data as SurveyData).totalLand
      : null;

  // Offense: freshest of CB/SOM that carries it — both stamp the identical raw quantity
  // (computeRawOffense), so "freshest wins" is strictly more correct than a type preference.
  const latestSom = freshestByType(reports, 'SOM');
  const offenseCandidates = [latestCb, latestSom].filter((r): r is IntelReportDto => r != null);
  const freshestOffenseReport = offenseCandidates.length === 0 ? undefined : freshestOf(offenseCandidates);
  const offensePoints = freshestOffenseReport
    ? freshestOffenseReport.type === 'CB'
      ? (parseIntelData(freshestOffenseReport).data as CrystalBallData).offensePoints
      : ((parseIntelData(freshestOffenseReport).data as SpyOnMilitaryData).offensePoints ?? null)
    : null;

  // Defense: CB only — SOM's defensePoints is a different, raw quantity and is never merged in.
  const defensePoints = latestCb ? (parseIntelData(latestCb).data as CrystalBallData).defensePoints : null;

  // Gold/Runes/Food/Trade Balance: CB-only.
  const cbData = latestCb ? (parseIntelData(latestCb).data as CrystalBallData) : null;
  const gold = cbData ? cbData.money : null;
  const runes = cbData ? cbData.runes : null;
  const food = cbData ? cbData.food : null;
  const tradeBalance = cbData ? cbData.tradeBalance : null;

  // Raw TPA: Infiltrate's rawTPA.
  const latestInfiltrate = freshestByType(reports, 'INFILTRATE');
  const rawTPA = latestInfiltrate ? (parseIntelData(latestInfiltrate).data as InfiltrateData).rawTPA : null;

  // Research Rate: Spy on Science's raw enum string (never an ordinal — render via formatEnumLabel()).
  const latestSos = freshestByType(reports, 'SOS');
  const researchRate = latestSos ? (parseIntelData(latestSos).data as SpyOnScienceData).researchRate : null;

  // Home Defense: Spy on Home Defense's homeDefense.
  const latestSohd = freshestByType(reports, 'SOHD');
  const homeDefense = latestSohd ? (parseIntelData(latestSohd).data as SpyOnHomeDefenseData).homeDefense : null;

  return {
    networth,
    land,
    offensePoints,
    defensePoints,
    gold,
    runes,
    food,
    tradeBalance,
    rawTPA,
    researchRate,
    homeDefense,
  };
}
